import { useCallback, useEffect, useState } from 'react';
import {
  adminApi, ApiError,
  type AdminTicketDto, type SeatDto, type SeatOccupancyDto, type SeatZoneDto,
} from '../api';

/**
 * TICK-103 — the organizer's side of the seat plan.
 *
 * The layout builder is a generator rather than a drawing tool on purpose (spec §3.5): rows ×
 * seats covers the great majority of theatres and halls, and a canvas editor is most of the
 * engineering cost of this feature for the venues it does not. What the organizer gets instead is
 * the room rendered immediately, exactly as the buyer will see it, with one click to block a chair.
 */

const DEFAULT_COLORS = ['#E8543F', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];

export function SeatPlanPanel({ token, eventSlug }: { token: string; eventSlug?: string }) {
  const [zones, setZones] = useState<SeatZoneDto[]>([]);
  const [tickets, setTickets] = useState<AdminTicketDto[]>([]);
  const [seats, setSeats] = useState<SeatDto[]>([]);
  const [occupancy, setOccupancy] = useState<SeatOccupancyDto[]>([]);
  const [activeZone, setActiveZone] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [draft, setDraft] = useState({
    ticketId: '', code: '', label: '', color: DEFAULT_COLORS[0],
    unitKind: 'seat' as 'seat' | 'table',
  });
  const [layout, setLayout] = useState({ rows: '10', seatsPerRow: '12', rowLabelStyle: 'alpha' as 'alpha' | 'numeric', aisleAfter: '' });

  const load = useCallback(async () => {
    try {
      const [z, t, o] = await Promise.all([
        adminApi.listSeatZones(token, eventSlug),
        adminApi.listTickets(token, eventSlug),
        adminApi.getSeatOccupancy(token, eventSlug),
      ]);
      setZones(z);
      setTickets(t);
      setOccupancy(o);
      setActiveZone((cur) => cur ?? z[0]?.id ?? null);
      setError('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'โหลดผังที่นั่งไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!activeZone) { setSeats([]); return; }
    adminApi.listSeats(token, activeZone, eventSlug).then(setSeats).catch(() => setSeats([]));
  }, [activeZone, token, eventSlug, occupancy]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'ดำเนินการไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  // Only a seated type can host a zone, and only one zone each — the two rules that keep a zone
  // from becoming a second pricing system.
  const eligible = tickets.filter(
    (t) => t.seatingMode === 'zone_seated' && !zones.some((z) => z.ticketId === t.id),
  );
  const zone = zones.find((z) => z.id === activeZone) ?? null;
  /*
   * TICK-105 §5.6 — a table zone must never show its unit count alone. `tickets.capacity` counts
   * units, so a room that admits 60 people reads "15" here, and an organizer who sees only that
   * number will think something is broken. Admissions come from the zone's ticket type, which is
   * the only place the number lives.
   */
  const unitSeats = zone ? (tickets.find((t) => t.id === zone.ticketId)?.seats ?? 1) : 1;
  const isTable = zone?.unitKind === 'table';
  const noun = isTable ? 'โต๊ะ' : 'ที่';
  const heldCodes = new Set(occupancy.filter((o) => o.orderCode).map((o) => o.seatCode));

  return (
    <div>
      {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}

      {tickets.every((t) => t.seatingMode !== 'zone_seated') && (
        <div className="admin-group" style={{ marginBottom: 12 }}>
          ยังไม่มีประเภทบัตรที่ตั้งเป็น "ระบุที่นั่ง" — ไปที่หน้า <strong>จัดการตั๋ว</strong> แล้วเปลี่ยนโหมดของบัตรก่อน
          (บัตรที่ระบุที่นั่งต้องเป็น 1 ใบ = 1 คน และเป็นบัตรขายหน้างานไม่ได้)
        </div>
      )}

      {eligible.length > 0 && (
        <div className="admin-group">
          <div className="card-step">เพิ่มโซน</div>
          <div className="ticket-edit-grid" style={{ marginTop: 10 }}>
            <label>
              ประเภทบัตร
              <select value={draft.ticketId} onChange={(e) => setDraft((d) => ({ ...d, ticketId: e.target.value }))}>
                <option value="">— เลือก —</option>
                {eligible.map((t) => <option key={t.id} value={t.id}>{t.nameEn}</option>)}
              </select>
            </label>
            <label>
              รหัสโซน (A-Z / 0-9)
              <input value={draft.code} maxLength={4} onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value.toUpperCase() }))} />
            </label>
            <label>
              ชื่อโซน
              <input value={draft.label} onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))} />
            </label>
            <label>
              สีโซน
              <input type="color" value={draft.color} onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))} />
            </label>
            <label>
              หน่วยที่ขาย
              <select
                value={draft.unitKind}
                onChange={(e) => setDraft((d) => ({ ...d, unitKind: e.target.value as 'seat' | 'table' }))}
              >
                <option value="seat">ที่นั่ง — 1 ใบ / 1 คน</option>
                <option value="table">โต๊ะ — 1 ใบ / หลายคน ตามจำนวนที่ตั้งไว้ในบัตร</option>
              </select>
            </label>
          </div>
          <div className="admin-actions" style={{ marginTop: 12 }}>
            <button
              className="btn-primary"
              style={{ padding: '8px 16px' }}
              disabled={busy || !draft.ticketId || !draft.code.trim()}
              onClick={() => run(async () => {
                await adminApi.createSeatZone(token, {
                  ticketId: draft.ticketId, code: draft.code.trim(), label: draft.label.trim(),
                  color: draft.color, unitKind: draft.unitKind,
                }, eventSlug);
                setDraft({
                  ticketId: '', code: '', label: '', unitKind: 'seat',
                  color: DEFAULT_COLORS[zones.length % DEFAULT_COLORS.length],
                });
              })}
            >
              สร้างโซน
            </button>
          </div>
        </div>
      )}

      {zones.length > 0 && (
        <div className="admin-group" style={{ marginTop: 12 }}>
          <div className="admin-subtabs">
            {zones.map((z) => (
              <button
                key={z.id}
                className={`admin-nav-btn ${activeZone === z.id ? 'active' : ''}`}
                onClick={() => setActiveZone(z.id)}
              >
                <i className="seatmap-swatch" style={{ background: z.color, marginRight: 6 }} />
                {z.code}{z.label ? ` · ${z.label}` : ''}
              </button>
            ))}
          </div>
        </div>
      )}

      {zone && (
        <>
          <div className="admin-group" style={{ marginTop: 12 }}>
            <div className="card-step">ตั้งค่าโซน {zone.code}</div>
            <div className="ticket-edit-grid" style={{ marginTop: 10 }}>
              <label>
                สีโซน
                <input
                  type="color" value={zone.color} disabled={busy}
                  onChange={(e) => run(() => adminApi.updateSeatZone(token, zone.id, { color: e.target.value }, eventSlug))}
                />
              </label>
              <label>
                วิธีเลือกที่นั่ง
                <select
                  value={zone.selectionMode} disabled={busy}
                  onChange={(e) => run(() => adminApi.updateSeatZone(
                    token, zone.id, { selectionMode: e.target.value as 'pick' | 'best_available' }, eventSlug,
                  ))}
                >
                  <option value="pick">ให้ลูกค้าเลือกเองบนผัง</option>
                  <option value="best_available">ระบบเลือกให้อัตโนมัติ</option>
                </select>
              </label>
            </div>
          </div>

          <div className="admin-group" style={{ marginTop: 12 }}>
            <div className="card-step">สร้างผังที่นั่ง</div>
            <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 8 }}>
              โควตาของบัตรจะถูกตั้งตามจำนวนที่นั่งที่ขายได้โดยอัตโนมัติ — สร้างผังใหม่ไม่ได้ถ้ามีคนจองที่นั่งในโซนนี้แล้ว
            </div>
            <div className="ticket-edit-grid">
              <label>
                จำนวนแถว
                <input type="number" min={1} max={100} value={layout.rows}
                  onChange={(e) => setLayout((l) => ({ ...l, rows: e.target.value }))} />
              </label>
              <label>
                ที่นั่งต่อแถว
                <input type="number" min={1} max={100} value={layout.seatsPerRow}
                  onChange={(e) => setLayout((l) => ({ ...l, seatsPerRow: e.target.value }))} />
              </label>
              <label>
                ป้ายแถว
                <select value={layout.rowLabelStyle}
                  onChange={(e) => setLayout((l) => ({ ...l, rowLabelStyle: e.target.value as 'alpha' | 'numeric' }))}>
                  <option value="alpha">A, B, C…</option>
                  <option value="numeric">1, 2, 3…</option>
                </select>
              </label>
              <label>
                เว้นทางเดินหลังที่นั่งที่ (คั่นด้วย ,)
                <input value={layout.aisleAfter} placeholder="เช่น 6, 12"
                  onChange={(e) => setLayout((l) => ({ ...l, aisleAfter: e.target.value }))} />
              </label>
            </div>
            <div className="admin-actions" style={{ marginTop: 12 }}>
              <button
                className="btn-primary" style={{ padding: '8px 16px' }} disabled={busy}
                onClick={() => run(() => adminApi.generateSeatLayout(token, zone.id, {
                  rows: Number(layout.rows) || 1,
                  seatsPerRow: Number(layout.seatsPerRow) || 1,
                  rowLabelStyle: layout.rowLabelStyle,
                  aisleAfter: layout.aisleAfter.split(',').map((v) => Number(v.trim())).filter((n) => n > 0),
                }, eventSlug))}
              >
                สร้างผัง ({(Number(layout.rows) || 0) * (Number(layout.seatsPerRow) || 0)} {isTable ? 'โต๊ะ' : 'ที่นั่ง'})
              </button>
            </div>
          </div>

          {seats.length > 0 && (
            <div className="admin-group" style={{ marginTop: 12 }}>
              <div className="card-step">ผัง{isTable ? 'โต๊ะ' : 'ที่นั่ง'} — คลิกเพื่อปิด/เปิดการขายราย{noun}</div>
              <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 10 }}>
                {isTable && (
                  <strong>{seats.length} โต๊ะ · จุได้ {seats.filter((s) => s.state !== 'blocked').length * unitSeats} คน · </strong>
                )}
                ขายได้ {seats.filter((s) => s.state !== 'blocked').length} {noun} · ถูกจองแล้ว {seats.filter((s) => s.state === 'taken').length} {noun} · ปิดขาย {seats.filter((s) => s.state === 'blocked').length} {noun}
              </div>
              <div className="seatmap-scroll">
                <div style={{ display: 'grid', gap: 4, gridTemplateColumns: `repeat(${Math.max(...seats.map((s) => s.x)) + 1}, 26px)` }}>
                  {seats.map((s) => (
                    <button
                      key={s.id}
                      className="seat-admin-cell"
                      title={`${s.seatCode}${heldCodes.has(s.seatCode) ? ' — ถูกจองแล้ว' : ''}`}
                      aria-label={`${s.seatCode} — ${s.state === 'blocked' ? 'ปิดการขาย' : s.state === 'taken' ? 'ถูกจองแล้ว' : 'ว่าง'}`}
                      disabled={busy || s.state === 'taken'}
                      style={{
                        gridColumn: s.x + 1,
                        gridRow: s.y + 1,
                        background: s.state === 'available' ? zone.color : 'transparent',
                        opacity: s.state === 'available' ? 1 : 0.35,
                        border: '1px solid currentColor',
                        borderRadius: 6,
                        height: 26,
                        cursor: s.state === 'taken' ? 'not-allowed' : 'pointer',
                      }}
                      onClick={() => run(() => adminApi.setSeatBlocked(token, s.id, s.state !== 'blocked', eventSlug))}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {occupancy.some((o) => o.orderCode) && (
            <div className="admin-group" style={{ marginTop: 12 }}>
              <div className="card-step">ใครนั่งที่ไหน</div>
              {occupancy.filter((o) => o.orderCode).map((o) => (
                <div key={o.seatCode} style={{ marginTop: 6, fontSize: 13 }}>
                  <strong>{o.seatCode}</strong> — {o.orderCode} ({o.orderStatus})
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
